import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import InputWithLabel from '@ui-parts/InputWithLabel';
import styled from 'styled-components';
import Button from '@ui-elements/Button';
import Link from '@mui/material/Link';
import { Typography } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { ROUTES } from '~/constants/routes';
import { PALETTE, Z_INDEX } from '~/assets/styles/base';
import LogoImage from '~/assets/images/logo-gmoask.png';
import { signinUser, callThunk } from '~/api';
import { emailValidationRule, signinPasswordValidationRule } from '~/constants/validationRules';
import { SESSION_STORAGES } from '~/constants';

const UserLoginFormContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const Img = styled.img`
  display: block;
  margin: 54px auto 20px auto;
  width: 258px;
`;

const InputCenterBox = styled.div`
  width: 300px;
  margin: 0 auto;
`;

const CenterBox = styled.div`
  text-align: center;
  margin: 0 auto;
`;

const LoginButton = styled(Button)`
  display: block;
  width: 280px;
  margin: 25px auto 20px auto;
`;

const RegisterBox = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 60px auto 0 auto;
  width: 350px;
`;

const RegisterTypography = styled(Typography)`
  line-height: 40px;
`;

const RegisterButton = styled(Button)`
  width: 150px;
  height: 40px;
`;

const StyledAlert = styled.div`
  width: max-content;
  line-height: 1.5;
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 11px 16px;
  margin: 31px auto 11px;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  white-space: pre-line;
  svg {
    font-size: 25px;
    margin: -2px 10px 0;
  }
`;

const IeraeLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 234px;
    height: 64px;
  }
  margin-top: 28px;

  @media (min-height: 767px) {
    margin-top: 32px;
  }

  @media (min-height: 899px) {
    margin-top: 166px;
  }
`;

const LoginBox = styled.div`
  margin-top: -116px;

  @media (min-height: 767px) {
    margin-top: -96px;
  }
`;

function Signin() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const validation = useSelector((state) => state.validation.signin);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data) => {
    dispatch(
      callThunk({
        action: signinUser,
        payload: data,
      })
    );
  };

  useEffect(() => {
    const redirectURL = sessionStorage.getItem(SESSION_STORAGES.USER_UNAUTHORIZED_ERROR_REFERRER);
    if (!validation?.errors?.authentication?.invalid && user.id !== '') {
      if (redirectURL) {
        navigate(redirectURL, {
          state: { referrer: window.location.pathname },
        });
      } else {
        navigate(ROUTES.USER.MYPAGE);
      }
    }
  }, [user.id]);

  return (
    <LoginBox>
      <form onSubmit={handleSubmit(onSubmit)}>
        <UserLoginFormContainer data-testid="User-Signin-UserLoginFormContainer">
          <Img src={LogoImage} alt="GMO Ask" title="GMO Ask" data-testid="User-Signin-Logo" />
          {validation?.errors?.authentication?.invalid && (
            <StyledAlert severity="error" color="error">
              <StyledTypography variant="body3">
                <WarningRoundedIcon />
                <span data-testid="User-Signin-Authentication-Error-Message">
                  {validation.errors.authentication.message}
                </span>
              </StyledTypography>
            </StyledAlert>
          )}
          <InputCenterBox>
            <Controller
              name="email"
              control={control}
              rules={emailValidationRule}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error },
              }) => {
                return (
                  <InputWithLabel
                    label={t('userSignin.email')}
                    name={name}
                    value={value}
                    errorMessage={error?.message}
                    width="300px"
                    handleChange={(e) => {
                      onChange(e);
                    }}
                    handleBlur={(e) => {
                      onBlur(e);
                    }}
                    error={Boolean(error)}
                    errorZIndex={Z_INDEX.tooltip}
                    dataTestId="User-Signin-email"
                    inputProps={{ maxLength: 100 }}
                    ref={ref}
                  />
                );
              }}
            />
            <Controller
              name="password"
              control={control}
              rules={signinPasswordValidationRule}
              render={({
                field: { onChange, onBlur, value, name, ref },
                fieldState: { error },
              }) => {
                return (
                  <InputWithLabel
                    label={t('userSignin.password')}
                    name={name}
                    type="password"
                    value={value}
                    errorMessage={error?.message}
                    width="300px"
                    handleChange={(e) => {
                      onChange(e);
                    }}
                    handleBlur={(e) => {
                      onBlur(e);
                    }}
                    error={Boolean(error)}
                    errorZIndex={Z_INDEX.tooltip}
                    dataTestId="User-Signin-password"
                    inputProps={{ maxLength: 24 }}
                    ref={ref}
                  />
                );
              }}
            />
          </InputCenterBox>
          <LoginButton variant="contained" type="submit" data-testid="User-Signin-LoginButton">
            {t('userSignin.login')}
          </LoginButton>
          <CenterBox>
            <Link
              component={RouterLink}
              to={ROUTES.USER.PASSWORD_RESET_REQUEST}
              underline="none"
              variant="body3"
              data-testid="User-Signin-ForgetPasswordLink"
            >
              {t('userSignin.forgetPassord')}
            </Link>
          </CenterBox>
        </UserLoginFormContainer>
        <RegisterBox>
          <RegisterTypography
            variant="body3"
            color="primary"
            data-testid="User-Signin-RegistrationRecommend"
          >
            {t('userSignin.registrationRecommend')}
          </RegisterTypography>
          <RegisterButton
            onClick={() => navigate(ROUTES.USER.SIGNUP)}
            data-testid="User-Signin-RegisterButton"
          >
            {t('userSignin.registration')}
          </RegisterButton>
        </RegisterBox>
        <IeraeLink data-testid="User-Signin-IeraeLink">
          <a
            href="https://shindan-lp.gmo-cybersecurity.com/?utm_source=gmo_research&utm_medium=02&utm_campaign=gmo_research_02_nds2023"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              decoding="async"
              loading="lazy"
              src="https://gmo-cybersecurity.com/nds/banner/rectangle2.jpg"
              alt="ネットde診断バナー"
            />
          </a>
        </IeraeLink>
      </form>
    </LoginBox>
  );
}

export default Signin;
